import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Search, X, Check, Ban, XCircle, Clock, Mail, User, Phone,
  MapPin, CalendarClock, Send, History, MessageSquarePlus,
} from "lucide-react";

const kanit = { fontFamily: "Kanit, Arial, sans-serif" };
const serif = { fontFamily: "'Noto Serif', Georgia, serif" };

const STATUS_META = {
  pending: { label: "Pending", bg: "#B3A17D", fg: "#2B1017" },
  confirmed: { label: "Confirmed", bg: "#183129", fg: "#FFFFFF" },
  rejected: { label: "Rejected", bg: "#8B3A3A", fg: "#FFFFFF" },
  cancelled: { label: "Cancelled", bg: "#9b8b7e", fg: "#FFFFFF" },
};
const FILTERS = ["all", "pending", "confirmed", "rejected", "cancelled"];

const fmt = (iso, tz = "Europe/Madrid") => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz, weekday: "short", day: "numeric", month: "short",
      year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, bg: "#897756", fg: "#fff" };
  return (
    <span
      data-testid={`fc-badge-${status}`}
      className="inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.14em] uppercase"
      style={{ ...kanit, backgroundColor: m.bg, color: m.fg }}
    >
      {m.label}
    </span>
  );
}

function EmailStatus({ emails }) {
  const entries = Object.entries(emails || {});
  if (!entries.length) return <span className="text-[13px] text-[#897756]" style={serif}>No notifications yet.</span>;
  const color = (st) => (st === "sent" ? "#183129" : st === "failed" ? "#8B3A3A" : "#897756");
  return (
    <div className="flex flex-wrap gap-2" data-testid="fc-email-status">
      {entries.map(([k, v]) => (
        <span key={k} className="text-[11px] px-2 py-0.5 border" style={{ ...kanit, borderColor: color(v?.status) + "55", color: color(v?.status) }}>
          {k}: {v?.status || "—"}
        </span>
      ))}
    </div>
  );
}

export function FreeCallPanel({ axios }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get("/admin/free-call/requests", { params });
      setItems(res.data.items || []);
      setCounts(res.data.counts || {});
    } catch {
      toast.error("Could not load Free Call requests.");
    } finally {
      setLoading(false);
    }
  }, [axios, filter, search]);

  useEffect(() => { load(); }, [load]);

  const refreshSelected = async (id) => {
    try {
      const res = await axios.get(`/admin/free-call/requests/${id}`);
      setSelected(res.data);
    } catch { /* ignore */ }
  };

  const act = async (id, action, payload) => {
    setBusy(true);
    try {
      const res = await axios.post(`/admin/free-call/requests/${id}/${action}`, payload || {});
      if (action === "resend-email") {
        toast.success("Notification is being resent.");
      } else {
        toast.success(`Request ${res.data.status}.`);
        setSelected(res.data);
      }
      setReason("");
      await load();
      if (action === "resend-email") await refreshSelected(id);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Action failed.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const addNote = async (id) => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const res = await axios.post(`/admin/free-call/requests/${id}/notes`, { content: note.trim() });
      setSelected(res.data);
      setNote("");
      toast.success("Note added.");
    } catch {
      toast.error("Could not add note.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="free-call-panel">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <h2 className="text-2xl" style={{ ...kanit, fontWeight: 600, color: "#451C25" }}>Free Call requests</h2>
          <p className="text-[13px] text-[#6B4954] mt-1" style={serif}>
            Requested times are preferences — confirm manually after checking availability.
          </p>
        </div>
        <button data-testid="fc-refresh" onClick={load} className="inline-flex items-center gap-2 border border-[#451C25]/25 px-4 py-2 text-[11px] tracking-[0.14em] uppercase hover:bg-[#451C25] hover:text-white transition-colors" style={kanit}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`fc-filter-${f}`}
            onClick={() => setFilter(f)}
            aria-pressed={filter === f}
            className={`px-3 py-1.5 text-[11px] tracking-[0.1em] uppercase border transition-colors ${
              filter === f ? "bg-[#451C25] text-white border-[#451C25]" : "border-[#451C25]/30 text-[#451C25] hover:border-[#451C25]"
            }`}
            style={kanit}
          >
            {f}{f !== "all" ? ` (${counts[f] || 0})` : ""}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#897756]" />
          <input
            data-testid="fc-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone"
            className="pl-9 pr-3 py-2 bg-white border border-[#451C25]/20 text-[14px] w-64 focus:outline-none focus:border-[#451C25]"
            style={serif}
          />
        </div>
      </div>

      {loading ? (
        <p data-testid="fc-loading" className="text-[#897756] py-10 text-center" style={serif}>Loading…</p>
      ) : items.length === 0 ? (
        <p data-testid="fc-empty" className="text-[#897756] py-10 text-center" style={serif}>No requests found.</p>
      ) : (
        <div className="grid gap-3" data-testid="fc-list">
          {items.map((r) => (
            <button
              key={r.id}
              data-testid={`fc-row-${r.id}`}
              onClick={() => { setSelected(r); setReason(""); setNote(""); }}
              className="text-left bg-white border border-[#451C25]/12 hover:border-[#451C25]/40 transition-colors p-4 flex items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <StatusBadge status={r.status} />
                  <span className="font-semibold text-[#451C25] truncate" style={kanit}>{r.full_name}</span>
                </div>
                <p className="text-[13px] text-[#6B4954] mt-1 truncate" style={serif}>{r.email}{r.phone ? ` · ${r.phone}` : ""}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[13px] text-[#451C25]" style={kanit}>{r.slot_label_local || fmt(r.slot_start_utc, r.operational_tz)}</p>
                <p className="text-[11px] text-[#897756]" style={serif}>{r.operational_tz}{r.interest ? ` · ${r.interest}` : ""}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div data-testid="fc-detail" role="dialog" aria-modal="true" className="fixed inset-0 z-[90] flex justify-end">
          <button aria-label="Close" onClick={() => setSelected(null)} className="absolute inset-0 bg-[#2B1017]/60" tabIndex={-1} />
          <div className="relative bg-[#F5EFE7] w-full max-w-lg h-full overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between p-5 border-b border-[#451C25]/10 sticky top-0 bg-[#F5EFE7] z-10">
              <div>
                <StatusBadge status={selected.status} />
                <h3 className="text-xl mt-2" style={{ ...kanit, fontWeight: 600, color: "#451C25" }}>{selected.full_name}</h3>
              </div>
              <button data-testid="fc-detail-close" onClick={() => setSelected(null)} className="w-10 h-10 flex items-center justify-center border border-[#451C25]/25 hover:bg-[#451C25] hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4" style={serif}>
              <Field icon={Mail} label="Email" value={selected.email} />
              {selected.phone && <Field icon={Phone} label="Phone" value={selected.phone} />}
              <Field icon={CalendarClock} label="Requested time" value={`${selected.slot_label_local || fmt(selected.slot_start_utc, selected.operational_tz)} (${selected.operational_tz})`} />
              <Field icon={Clock} label="UTC instant" value={selected.slot_start_utc} />
              {selected.visitor_tz && <Field icon={MapPin} label="Visitor timezone" value={selected.visitor_tz} />}
              {selected.interest && <Field icon={User} label="Interest" value={selected.interest} />}
              {selected.country_interest && <Field icon={MapPin} label="Country of interest" value={selected.country_interest} />}
              {selected.origin && <Field icon={MapPin} label="Origin" value={selected.origin} />}
              {selected.note && <Field icon={MessageSquarePlus} label="Note" value={selected.note} />}
              <Field icon={Clock} label="Received" value={fmt(selected.created_at, selected.operational_tz)} />
              {selected.decided_by && <Field icon={User} label="Decided by" value={`${selected.decided_by} · ${fmt(selected.decided_at, selected.operational_tz)}`} />}

              <div>
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#897756] mb-1.5" style={kanit}>Notifications</p>
                <EmailStatus emails={selected.emails} />
                <button data-testid="fc-resend-email" disabled={busy} onClick={() => act(selected.id, "resend-email")} className="mt-2 inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase text-[#6B4954] hover:text-[#451C25] disabled:opacity-50" style={kanit}>
                  <Send size={13} /> Resend current-status email
                </button>
              </div>

              {/* Reason (used by reject / cancel; optional) */}
              {(selected.status === "pending" || selected.status === "confirmed") && (
                <div>
                  <label className="text-[10px] tracking-[0.22em] uppercase text-[#897756]" style={kanit}>Reason (optional, for reject/cancel)</label>
                  <textarea data-testid="fc-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-1 w-full bg-white border border-[#451C25]/20 px-3 py-2 text-[14px] focus:outline-none focus:border-[#451C25]" />
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                {selected.status === "pending" && (
                  <>
                    <ActionBtn testid="fc-confirm" icon={Check} label="Confirm" primary disabled={busy} onClick={() => act(selected.id, "confirm", { reason: reason || null })} />
                    <ActionBtn testid="fc-reject" icon={Ban} label="Reject" disabled={busy} onClick={() => act(selected.id, "reject", { reason: reason || null })} />
                    <ActionBtn testid="fc-cancel" icon={XCircle} label="Cancel" disabled={busy} onClick={() => act(selected.id, "cancel", { reason: reason || null })} />
                  </>
                )}
                {selected.status === "confirmed" && (
                  <ActionBtn testid="fc-cancel" icon={XCircle} label="Cancel confirmed call" disabled={busy} onClick={() => act(selected.id, "cancel", { reason: reason || null })} />
                )}
              </div>

              {/* History */}
              <div className="pt-2">
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#897756] mb-2 flex items-center gap-1.5" style={kanit}><History size={13} /> History</p>
                <ul className="space-y-1.5" data-testid="fc-history">
                  {(selected.history || []).map((h, i) => (
                    <li key={i} className="text-[13px] text-[#451C25] border-l-2 border-[#B3A17D]/50 pl-3">
                      <strong>{h.from_status || "—"} → {h.to_status}</strong> · {h.by || "—"} · {fmt(h.at, selected.operational_tz)}
                      {h.reason ? <span className="text-[#897756]"> · {h.reason}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Internal notes */}
              <div className="pt-2">
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#897756] mb-2" style={kanit}>Internal notes</p>
                <ul className="space-y-1.5 mb-2">
                  {(selected.notes || []).map((n, i) => (
                    <li key={i} className="text-[13px] text-[#451C25] bg-white border border-[#451C25]/10 px-3 py-2">
                      {n.content}
                      <span className="block text-[11px] text-[#897756] mt-0.5">{n.author_email} · {fmt(n.created_at, selected.operational_tz)}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <input data-testid="fc-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note" className="flex-1 bg-white border border-[#451C25]/20 px-3 py-2 text-[14px] focus:outline-none focus:border-[#451C25]" />
                  <button data-testid="fc-note-add" disabled={busy || !note.trim()} onClick={() => addNote(selected.id)} className="px-4 py-2 bg-[#451C25] text-white text-[11px] tracking-[0.12em] uppercase disabled:opacity-50" style={kanit}>Add</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-[#897756] shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#897756]" style={kanit}>{label}</p>
        <p className="text-[14px] text-[#451C25] break-words">{value || "—"}</p>
      </div>
    </div>
  );
}

function ActionBtn({ testid, icon: Icon, label, onClick, disabled, primary }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-4 py-2 text-[11px] tracking-[0.14em] uppercase transition-colors disabled:opacity-50 ${
        primary ? "bg-[#183129] text-white hover:bg-[#0f1f19]" : "border border-[#451C25]/30 text-[#451C25] hover:bg-[#451C25] hover:text-white"
      }`}
      style={kanit}
    >
      <Icon size={14} /> {label}
    </button>
  );
}

export default FreeCallPanel;
